import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import StaffRatioWidget from '@/components/StaffRatioWidget';
import ClassroomsWidget from '@/components/ClassroomsWidget';
import CertificationAlertsWidget from '@/components/CertificationAlertsWidget';
import ChildFormsComplianceWidget from '@/components/ChildFormsComplianceWidget';
import PaymentStatusWidget from '@/components/PaymentStatusWidget';
import VansTodayWidget from '@/components/VansTodayWidget';
import ApplicationsWidget from '@/components/ApplicationsWidget';
import { AppAdoptionWidget } from '@/components/AppAdoptionWidget';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Baby, Users, UserCheck, Shield, DollarSign, 
  MessageSquare, TrendingUp, AlertCircle, CheckCircle,
  Clock, Calendar, CreditCard, ArrowUpRight, Heart, AlertTriangle, ArrowRightLeft, Flame
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
import useAutoRefresh from '@/hooks/useAutoRefresh';

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [healthReminders, setHealthReminders] = useState([]);
  const [todayMoves, setTodayMoves] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [showDeficiencies, setShowDeficiencies] = useState(false);
  const [dashboardAlerts, setDashboardAlerts] = useState([]);
  const [safetyDrillStatus, setSafetyDrillStatus] = useState([]);
  const [editingAlertIdx, setEditingAlertIdx] = useState(null);
  const [alertDate, setAlertDate] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Auto-refresh alerts when tab regains focus + every 60s — so that
  // compliance reminders disappear immediately after admin updates a
  // profile in another tab, without requiring a manual reload.
  useAutoRefresh(() => { fetchDashboardData(); }, 60000);

  const fetchDashboardData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [statsRes, attendanceRes, healthRes, movesRes, complianceRes, alertsRes, safetyRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/attendance/today`, getAuthHeaders()),
        axios.get(`${API}/health-report-reminders`, getAuthHeaders()).catch(() => ({ data: { reminders: [] } })),
        axios.get(`${API}/attendance/moves-report?start_date=${today}&end_date=${today}`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/compliance`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/alerts`, getAuthHeaders()).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/safety-drills/status`, getAuthHeaders()).catch(() => ({ data: { status: [] } }))
      ]);
      setStats(statsRes.data);
      setRecentAttendance(attendanceRes.data.slice(0, 5));
      setHealthReminders(healthRes.data.reminders || []);
      setTodayMoves(movesRes.data || []);
      setComplianceItems(complianceRes.data || []);
      setDashboardAlerts(alertsRes.data.alerts || []);
      setSafetyDrillStatus(safetyRes.data.status || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const seedCompliance = async () => {
    try {
      await axios.post(`${API}/compliance/seed`, {}, getAuthHeaders());
      toast.success('Compliance items seeded successfully');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to seed compliance items');
    }
  };

  const handleAlertDateSave = async (alert) => {
    if (!alertDate || !alert.staff_id || !alert.field_key) return;
    try {
      await axios.put(`${API}/staff/${alert.staff_id}`, { [alert.field_key]: alertDate }, getAuthHeaders());
      toast.success(`${alert.title} — date saved`);
      setEditingAlertIdx(null);
      setAlertDate('');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to save date');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    { 
      title: 'Total Children', 
      value: stats?.total_children || 0, 
      icon: <Baby className="w-6 h-6" />,
      color: 'bg-primary/10 text-primary',
      link: '/children'
    },
    { 
      title: 'Checked In Today', 
      value: stats?.checked_in_today || 0, 
      icon: <Clock className="w-6 h-6" />,
      color: 'bg-green-100 text-green-600',
      link: '/attendance'
    },
    { 
      title: 'Total Staff', 
      value: stats?.total_staff || 0, 
      icon: <UserCheck className="w-6 h-6" />,
      color: 'bg-blue-100 text-blue-600',
      link: '/staff'
    },
    { 
      title: 'Total Parents', 
      value: stats?.total_parents || 0, 
      icon: <Users className="w-6 h-6" />,
      color: 'bg-purple-100 text-purple-600',
      link: '/parents'
    },
    { 
      title: 'Pending Invoices', 
      value: stats?.pending_invoices || 0, 
      icon: <DollarSign className="w-6 h-6" />,
      color: 'bg-amber-100 text-amber-600',
      link: '/billing'
    },
    { 
      title: 'Unread Messages', 
      value: stats?.unread_messages || 0, 
      icon: <MessageSquare className="w-6 h-6" />,
      color: 'bg-rose-100 text-rose-600',
      link: '/messages'
    },
  ];

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <Card 
              key={index} 
              className="card-clay cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200" 
              data-testid={`stat-card-${index}`}
              onClick={() => navigate(stat.link)}
            >
              <CardContent className="p-4">
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                  {stat.icon}
                </div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Upcoming Alerts Banner (60-day lookahead) */}
        {dashboardAlerts.length > 0 && (
          <Card className="border-l-4 border-l-red-500 bg-red-50/50" data-testid="dashboard-alerts-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-800">
                    {dashboardAlerts.length} Alert{dashboardAlerts.length > 1 ? 's' : ''} — Items Due Within 60 Days
                  </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/compliance')} data-testid="manage-alerts-btn">
                  Manage
                  <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {dashboardAlerts.map((alert, idx) => (
                  <div key={idx} className={cn(
                    "p-2 rounded-lg border text-sm",
                    alert.urgency === 'overdue' ? 'bg-red-100 border-red-300' :
                    alert.urgency === 'critical' ? 'bg-orange-100 border-orange-300' :
                    'bg-amber-50 border-amber-200'
                  )}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <AlertCircle className={cn("w-4 h-4 shrink-0",
                          alert.urgency === 'overdue' ? 'text-red-600' :
                          alert.urgency === 'critical' ? 'text-orange-600' : 'text-amber-600'
                        )} />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{alert.title}</p>
                          <div className="flex items-center gap-2 text-xs">
                            {alert.expires_on ? (
                              <span className={cn(
                                "font-semibold",
                                alert.urgency === 'overdue' ? 'text-red-700' :
                                alert.urgency === 'critical' ? 'text-orange-700' : 'text-amber-700'
                              )}>
                                {alert.days_until < 0 ? 'Was due' : 'Due'} {new Date(alert.expires_on + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            ) : null}
                            <p className="text-muted-foreground truncate">{alert.detail}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {alert.type === 'staff_cert' && editingAlertIdx !== idx && (
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={(e) => { e.stopPropagation(); setEditingAlertIdx(idx); setAlertDate(''); }} data-testid={`alert-set-date-${idx}`}>
                            Set Date
                          </Button>
                        )}
                        <Badge className={cn("shrink-0",
                          alert.urgency === 'overdue' ? 'bg-red-600' :
                          alert.urgency === 'critical' ? 'bg-orange-600' : 'bg-amber-500'
                        )}>
                          {alert.status_label || (alert.days_until < 0 ? 'OUT OF COMPLIANCE' : 'DUE SOON')}
                        </Badge>
                      </div>
                    </div>
                    {editingAlertIdx === idx && alert.type === 'staff_cert' && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                        <Input type="date" value={alertDate} onChange={(e) => setAlertDate(e.target.value)} className="h-7 text-xs w-36" data-testid={`alert-date-input-${idx}`} />
                        <Button size="sm" className="h-7 text-xs" onClick={() => handleAlertDateSave(alert)} disabled={!alertDate} data-testid={`alert-date-save-${idx}`}>Save</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingAlertIdx(null)}>Cancel</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Health Report Alerts */}
        {healthReminders.length > 0 && (
          <Card 
            className={cn(
              "border-l-4 cursor-pointer hover:shadow-lg transition-shadow",
              healthReminders.some(r => r.urgency === 'overdue') 
                ? "border-l-red-500 bg-red-50/50" 
                : "border-l-amber-500 bg-amber-50/50"
            )}
            onClick={() => navigate('/compliance')}
            data-testid="health-report-alert"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {healthReminders.some(r => r.urgency === 'overdue') ? (
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                      <Heart className="w-5 h-5 text-amber-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-sm">
                      {healthReminders.filter(r => r.urgency === 'overdue').length > 0 
                        ? `${healthReminders.filter(r => r.urgency === 'overdue').length} Overdue Health Report${healthReminders.filter(r => r.urgency === 'overdue').length > 1 ? 's' : ''}`
                        : `${healthReminders.length} Health Report Reminder${healthReminders.length > 1 ? 's' : ''}`
                      }
                    </p>
                    <div className="flex flex-col gap-0.5 mt-0.5">
                      {healthReminders.slice(0, 3).map((r) => (
                        <p key={r.id} className="text-xs text-muted-foreground">
                          <span className="font-medium">{r.child_name}</span>
                          {r.due_date && (
                            <span className={cn(
                              "ml-2 font-semibold",
                              r.urgency === 'overdue' ? 'text-red-700'
                                : r.urgency === 'warning' ? 'text-amber-700'
                                : 'text-blue-700'
                            )}>
                              Due {new Date(r.due_date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </p>
                      ))}
                      {healthReminders.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{healthReminders.length - 3} more</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">DCF-F-CFS0060 Child Health Report</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0" data-testid="view-health-alerts-btn">
                  View Details
                  <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safety Drill Alerts */}
        {(() => {
          const dueNow = safetyDrillStatus.filter(s => s.current_month_due && !s.optional);
          const missedPrior = safetyDrillStatus.filter(s => (s.missing_months || []).some(m => m < new Date().getMonth() + 1));
          const hasAny = dueNow.length > 0 || missedPrior.length > 0;
          if (!hasAny) return null;
          const hasOverdue = missedPrior.length > 0;
          return (
            <Card
              className={cn(
                "border-l-4 cursor-pointer hover:shadow-lg transition-shadow",
                hasOverdue ? "border-l-red-500 bg-red-50/50" : "border-l-amber-500 bg-amber-50/50"
              )}
              onClick={() => navigate('/safety-drills')}
              data-testid="safety-drill-alert"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                      hasOverdue ? "bg-red-100" : "bg-amber-100"
                    )}>
                      <Flame className={cn("w-5 h-5", hasOverdue ? "text-red-600" : "text-amber-600")} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">
                        {hasOverdue
                          ? `${missedPrior.length} Safety Drill${missedPrior.length > 1 ? 's' : ''} Overdue`
                          : `${dueNow.length} Safety Drill${dueNow.length > 1 ? 's' : ''} Due This Month`}
                      </p>
                      <div className="flex flex-col gap-0.5 mt-0.5">
                        {dueNow.slice(0, 3).map((s) => (
                          <p key={s.type} className="text-xs text-muted-foreground">
                            <span className="font-medium">{s.label}</span>
                            <span className="ml-2 font-semibold text-amber-700">
                              Due this month
                            </span>
                          </p>
                        ))}
                        {missedPrior.slice(0, 2).map((s) => {
                          const miss = (s.missing_months || []).filter(m => m < new Date().getMonth() + 1);
                          const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          return (
                            <p key={`p-${s.type}`} className="text-xs text-muted-foreground">
                              <span className="font-medium">{s.label}</span>
                              <span className="ml-2 font-semibold text-red-700">
                                Missed: {miss.map(m => names[m]).join(', ')}
                              </span>
                            </p>
                          );
                        })}
                        <p className="text-xs text-muted-foreground mt-0.5">DCF 251.06 Safety &amp; Emergency</p>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0" data-testid="view-safety-drills-btn">
                    View Details
                    <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Room Transitions Today */}
        {todayMoves.length > 0 && (
          <Card 
            className="border-l-4 border-l-indigo-500 bg-indigo-50/50 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/attendance')}
            data-testid="room-transitions-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                    <ArrowRightLeft className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {todayMoves.length} Room Transition{todayMoves.length > 1 ? 's' : ''} Today
                    </p>
                    <div className="flex flex-col gap-1 mt-1">
                      {todayMoves.slice(0, 3).map((mv, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                          {mv.child_id ? (
                            <img
                              src={`${API}/children/${mv.child_id}/photo`}
                              alt={mv.child_name}
                              className="w-6 h-6 rounded-full object-cover bg-primary/10"
                              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                              data-testid={`transition-child-photo-${mv.child_id}`}
                            />
                          ) : null}
                          <div
                            className="w-6 h-6 rounded-full bg-primary/10 items-center justify-center text-[9px] font-bold text-primary"
                            style={{ display: mv.child_id ? 'none' : 'flex' }}
                          >
                            {(mv.child_name || '??').split(' ').map(s => s[0]).slice(0, 2).join('')}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">{mv.child_name}</span>
                            {' '}{mv.from_classroom} → {mv.to_classroom}
                            {' at '}{new Date(mv.move_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                          </p>
                        </div>
                      ))}
                      {todayMoves.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{todayMoves.length - 3} more</p>
                      )}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0" data-testid="view-transitions-btn">
                  View All
                  <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Staff Ratio Widget - NEW */}
          <div className="lg:col-span-1">
            <StaffRatioWidget />
          </div>

          <div className="lg:col-span-2 space-y-6">
            {/* Compliance Overview */}
            <Card className="card-clay cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowDeficiencies(!showDeficiencies)} data-testid="compliance-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  DCF Compliance
                </CardTitle>
                <div className="flex items-center gap-2">
                  {stats?.compliance_total === 0 && (
                    <Button 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); seedCompliance(); }}
                      data-testid="seed-compliance-btn"
                    >
                      Initialize Items
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate('/compliance'); }} data-testid="view-compliance-btn">
                    View All
                    <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Overall Compliance</span>
                    <span className="font-bold text-lg">{stats?.compliance_percentage || 0}%</span>
                  </div>
                  <Progress value={stats?.compliance_percentage || 0} className="h-3" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {stats?.compliance_completed || 0} Completed
                    </span>
                    <span className="flex items-center gap-1 text-amber-600">
                      <AlertCircle className="w-4 h-4" />
                      {(stats?.compliance_total || 0) - (stats?.compliance_completed || 0)} Pending
                    </span>
                  </div>
                  {/* Deficiencies List */}
                  {showDeficiencies && (() => {
                    const pending = complianceItems.filter(i => !i.is_completed);
                    return pending.length > 0 ? (
                      <div className="border-t pt-3 mt-2 space-y-2">
                        <p className="text-xs font-semibold text-amber-700">Deficiencies / Pending Items:</p>
                        {pending.map((item) => (
                          <div key={item.id} className="flex items-start gap-2 text-sm p-2 bg-amber-50 rounded-lg border border-amber-100">
                            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-amber-800">{item.title}</p>
                              <p className="text-xs text-amber-600">{item.regulation_code || item.category}
                                {item.reset_days > 0 && ` — Resets every ${item.reset_days === 1 ? 'day' : item.reset_days === 30 ? 'month' : item.reset_days === 365 ? 'year' : item.reset_days === 730 ? '2 years' : item.reset_days === 1460 ? '4 years' : `${item.reset_days} days`}`}
                              </p>
                              {item.description && <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>}
                              {item.last_completed_date && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Last completed: {new Date(item.last_completed_date + 'T12:00:00').toLocaleDateString()}
                                  {item.last_completed_by && ` by ${item.last_completed_by}`}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" className="w-full mt-1" onClick={(e) => { e.stopPropagation(); navigate('/compliance'); }}>
                          Manage Compliance Items
                        </Button>
                      </div>
                    ) : (
                      <div className="border-t pt-3 mt-2 text-center text-sm text-green-600">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1" />
                        All compliance items complete!
                      </div>
                    );
                  })()}
                  {!showDeficiencies && complianceItems.some(i => !i.is_completed) && (
                    <p className="text-xs text-muted-foreground text-center">Click to view deficiencies</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Today's Attendance */}
            <Card className="card-clay">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Today&apos;s Attendance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentAttendance.length > 0 ? (
                  <div className="space-y-3">
                    {recentAttendance.map((record, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          {record.child_id ? (
                            <img
                              src={`${API}/children/${record.child_id}/photo`}
                              alt={record.child_name}
                              className="w-10 h-10 rounded-full object-cover bg-primary/10"
                              data-testid={`attendance-child-photo-${record.child_id}`}
                              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div
                            className="w-10 h-10 rounded-full bg-primary/10 items-center justify-center text-sm font-bold text-primary"
                            style={{ display: record.child_id ? 'none' : 'flex' }}
                          >
                            {(record.child_name || '??').split(' ').map(s => s[0]).slice(0, 2).join('')}
                          </div>
                          <div>
                            <p className="font-medium">{record.child_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {record.check_in_time && new Date(record.check_in_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.check_out_time 
                            ? 'bg-gray-100 text-gray-600' 
                            : 'bg-green-100 text-green-600'
                        }`}>
                          {record.check_out_time ? 'Checked Out' : 'Present'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No attendance records today</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Classrooms Section */}
        <ClassroomsWidget />

        {/* Vans Today - live driver route summary */}
        <VansTodayWidget />

        <AppAdoptionWidget />

        {/* Applications - staff & child application review shortcut */}
        <ApplicationsWidget />

        {/* Children's Forms Compliance Widget */}
        <ChildFormsComplianceWidget />

        {/* Payment Status Widget */}
        <PaymentStatusWidget />

        {/* Certification Alerts Section */}
        <CertificationAlertsWidget getAuthHeaders={getAuthHeaders} />

        {/* Quick Actions */}
        <Card className="card-clay">
          <CardHeader>
            <CardTitle className="font-heading">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => window.location.href = '/children'}
                data-testid="quick-action-children"
              >
                <Baby className="w-6 h-6 text-primary" />
                <span>Add Child</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => window.location.href = '/staff'}
                data-testid="quick-action-staff"
              >
                <UserCheck className="w-6 h-6 text-primary" />
                <span>Add Staff</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => window.location.href = '/compliance'}
                data-testid="quick-action-compliance"
              >
                <Shield className="w-6 h-6 text-primary" />
                <span>Compliance</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => window.location.href = '/billing'}
                data-testid="quick-action-billing"
              >
                <DollarSign className="w-6 h-6 text-primary" />
                <span>Create Invoice</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
